// Place in src/utils/fieldConnections.js.
// Connection metadata lives on PDF widgets; synthetic inputs never enter the PDF.
const own = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);
const text = (value) => String(value ?? "");
export const getConnectionInputName = (sourceId) =>
  `connected_input_${text(sourceId).replace(/[^a-zA-Z0-9_]/g, "_")}`;

export const isConnectedCharacterField = (field) => {
  const connection = field?.connection;

  // Normal fields do not have connection metadata.
  if (!connection || typeof connection !== "object") {
    return false;
  }

  return (
    connection.mode === "character" &&
    typeof connection.source_field_id === "string" &&
    connection.source_field_id.length > 0 &&
    typeof connection.group_id === "string" &&
    connection.group_id.length > 0 &&
    Number.isInteger(connection.character_index) &&
    connection.character_index >= 0 &&
    Number.isInteger(connection.character_count) &&
    connection.character_count > connection.character_index
  );
};

export const isConnectionSourceField = (field) =>
  field?.connection_source === true;

const alphanumericCharacters = (value) =>
  Array.from(text(value)).filter((character) =>
    /[\p{L}\p{N}]/u.test(character),
  );

const toCharacters = (value, stripSeparators, staticPrefix = "") => {
  const characters = stripSeparators
    ? alphanumericCharacters(value)
    : Array.from(text(value));
  const prefixCharacters = stripSeparators
    ? alphanumericCharacters(staticPrefix)
    : Array.from(text(staticPrefix));

  if (
    prefixCharacters.length > 0 &&
    prefixCharacters.every(
      (character, index) =>
        characters[index]?.toLocaleLowerCase() ===
        character.toLocaleLowerCase(),
    )
  ) {
    return characters.slice(prefixCharacters.length);
  }

  return characters;
};

function collectSources(fields = []) {
  const sources = new Map();
  for (const field of fields) {
    if (!isConnectedCharacterField(field)) continue;
    const c = field.connection;
    if (!sources.has(c.source_field_id)) sources.set(c.source_field_id, []);
    sources.get(c.source_field_id).push(field);
  }
  return sources;
}

export function getConnectedSourceFields(fields = []) {
  return [...collectSources(fields)].map(([sourceId, targets]) => {
    const ordered = [...targets].sort(
      (a, b) =>
        a.connection.group_id.localeCompare(b.connection.group_id) ||
        a.connection.character_index - b.connection.character_index,
    );
    const first = ordered[0];
    const c = first.connection;
    const actualSource = fields.find(
      (f) => !isConnectedCharacterField(f) && f.field_id === sourceId,
    );
    const savedSource = ordered.find((f) => own(f.connection, "source_value"));
    const firstGroup = ordered.filter(
      (f) => f.connection.group_id === c.group_id,
    );
    const byIndex = new Map(
      firstGroup.map((f) => [f.connection.character_index, f]),
    );
    const reconstructedCharacters = Array.from(
      { length: c.character_count },
      (_, i) => Array.from(text(byIndex.get(i)?.value))[0] ?? " ",
    )
      .join("")
      .trimEnd();
    const reconstructed = `${text(c.static_prefix)}${reconstructedCharacters}`;
    const value = savedSource
      ? text(savedSource.connection.source_value)
      : actualSource && text(actualSource.value) !== ""
        ? text(actualSource.value)
        : reconstructed;
    const label =
      c.source_field_label || actualSource?.field_label || "Connected field";
    return {
      field_id: sourceId,
      name: getConnectionInputName(sourceId),
      field_label: label,
      label,
      type: "text",
      field_type: "text",
      location: "create",
      value,
      connection_source: true,
      connection_capacity: Math.min(
        ...targets.map((f) => f.connection.character_count),
      ),
      connection_strip_separators: Boolean(c.strip_separators),
      connection_static_prefix: text(c.static_prefix),
      required: targets.some((f) => f.field_required || f.required),
      field_required: targets.some((f) => f.field_required || f.required),
      block_id: actualSource?.block_id || first.block_id || "",
      block_label: actualSource?.block_label || first.block_label || "",
      order: Math.min(
        ...targets.map((f) => Number(f.order)).filter(Number.isFinite),
      ),
    };
  });
}

export function applyConnectedSourceDefaults(
  fields = [],
  values = {},
  fieldDefinitions = [],
  sharedValues = {},
) {
  const next = { ...values };
  const definitionsById = new Map(
    fieldDefinitions
      .filter((field) => field?.field_id)
      .map((field) => [field.field_id, field]),
  );

  getConnectedSourceFields(fields).forEach((source) => {
    const definition = definitionsById.get(source.field_id);
    const semanticName = definition?.field_name;
    const currentValue = next[source.name];

    if (text(currentValue) !== "") return;

    const defaultValue = semanticName
      ? own(next, semanticName) && text(next[semanticName]) !== ""
        ? next[semanticName]
        : text(sharedValues?.[source.field_id]) !== ""
          ? sharedValues[source.field_id]
          : sharedValues?.[semanticName]
      : sharedValues?.[source.field_id];

    if (
      defaultValue === undefined ||
      defaultValue === null ||
      defaultValue === ""
    ) {
      return;
    }

    next[source.name] = defaultValue;
    if (
      semanticName &&
      (!own(next, semanticName) || text(next[semanticName]) === "")
    ) {
      next[semanticName] = defaultValue;
    }
  });

  return withConnectedCharacterValues(fields, next, { strict: false });
}

export function getConnectedDisplayFields(fields = []) {
  const sources = getConnectedSourceFields(fields);
  if (!sources.length) return fields;
  const sourceIds = new Set(sources.map((f) => f.field_id));
  return [
    ...fields.filter(
      (f) => !isConnectedCharacterField(f) && !sourceIds.has(f.field_id),
    ),
    ...sources,
  ];
}

export function getConnectionInputError(field, value) {
  const length = toCharacters(
    value,
    field.connection_strip_separators,
    field.connection_static_prefix,
  ).length;
  return length > field.connection_capacity
    ? `${field.label} has ${field.connection_capacity} character boxes; you entered ${length} characters.`
    : "";
}

export function validateConnectionGroups(fields = []) {
  const groups = new Map();
  const sourceModes = new Map();
  for (const field of fields) {
    if (!isConnectedCharacterField(field)) continue;
    const c = field.connection;
    const mode = Boolean(c.strip_separators);
    if (
      sourceModes.has(c.source_field_id) &&
      sourceModes.get(c.source_field_id) !== mode
    ) {
      throw new Error(
        "Connections to the same source must use the same separator setting.",
      );
    }
    sourceModes.set(c.source_field_id, mode);
    if (!groups.has(c.group_id)) groups.set(c.group_id, []);
    groups.get(c.group_id).push(field);
  }
  for (const targets of groups.values()) {
    const c = targets[0].connection;
    const staticPrefix = text(c.static_prefix);
    const indexes = new Set(targets.map((f) => f.connection.character_index));
    if (
      targets.length !== c.character_count ||
      indexes.size !== targets.length ||
      targets.some(
        (f) =>
          f.connection.source_field_id !== c.source_field_id ||
          f.connection.character_count !== c.character_count ||
          text(f.connection.static_prefix) !== staticPrefix,
      )
    ) {
      throw new Error(
        `Reconnect all boxes for ${c.source_field_label || "this field"}; the connection is incomplete.`,
      );
    }
  }
}

export function applyConnectedCharacterValues(
  fields = [],
  values = {},
  { strict = true } = {},
) {
  if (!fields.some(isConnectedCharacterField)) return fields;
  if (strict) validateConnectionGroups(fields);
  const sourceValues = new Map();
  for (const source of getConnectedSourceFields(fields)) {
    const actual = fields.find(
      (f) =>
        !isConnectedCharacterField(f) &&
        f.field_id === source.field_id &&
        own(values, f.name),
    );
    const value = own(values, source.name)
      ? text(values[source.name])
      : actual
        ? text(values[actual.name])
        : source.value;
    const error = getConnectionInputError(source, value);
    if (strict && error) throw new Error(error);
    if (
      strict &&
      source.required &&
      toCharacters(
        value,
        source.connection_strip_separators,
        source.connection_static_prefix,
      ).length === 0
    ) {
      throw new Error(`${source.label} is required.`);
    }
    sourceValues.set(source.field_id, value);
  }
  return fields.map((field) => {
    if (isConnectedCharacterField(field)) {
      const c = field.connection;
      const value = sourceValues.get(c.source_field_id);
      return {
        ...field,
        value:
          toCharacters(value, c.strip_separators, c.static_prefix)[
            c.character_index
          ] ?? "",
        connection: { ...c, source_value: value },
      };
    }
    return sourceValues.has(field.field_id)
      ? { ...field, value: sourceValues.get(field.field_id) }
      : field;
  });
}

export function withConnectedCharacterValues(
  fields = [],
  values = {},
  options,
) {
  const resolved = applyConnectedCharacterValues(fields, values, options);
  const next = { ...values };
  const sourceIds = new Set(
    getConnectedSourceFields(fields).map((f) => f.field_id),
  );
  resolved.forEach((f) => {
    if (f.name && (isConnectedCharacterField(f) || sourceIds.has(f.field_id)))
      next[f.name] = f.value;
  });
  return next;
}

// orderedTargets must come from the current raw widget array, not a field_id-deduped array.
export function connectCharacterFields(
  fields,
  orderedTargets,
  source,
  { groupId, stripSeparators = false, staticPrefix = "" } = {},
) {
  if (!source?.field_id || !groupId || !orderedTargets.length)
    throw new Error("Select a source and at least one character box.");
  const sourceType = String(
    source.field_type ||
      (source.type === "PDFTextField" ? "text" : source.type) ||
      "",
  ).toLowerCase();

  if (
    source.generic_table ||
    source.connection ||
    source.text_connection ||
    source.location === "sign" ||
    (sourceType && !["text", "number", "multiline"].includes(sourceType))
  ) {
    throw new Error("Select a text or number field as the source.");
  }
  const key = (f) => f.field_instance_id || f.name;
  const targetKeys = new Set(orderedTargets.map(key));
  if (targetKeys.has(undefined) || targetKeys.size !== orderedTargets.length)
    throw new Error("Selected boxes need unique instance IDs or names.");
  const live = new Map(fields.map((f) => [key(f), f]));
  const targets = orderedTargets.map((f) => live.get(key(f)));
  if (
    targets.some(
      (f) =>
        !f ||
        f.type !== "PDFTextField" ||
        !f.field_id ||
        f.generic_table ||
        f.text_connection ||
        f.location === "sign" ||
        (f.connection && !isConnectedCharacterField(f)) ||
        f.field_id === source.field_id,
    )
  ) {
    throw new Error(
      "Select tagged PDF text boxes outside tables and text-flow connections. The source must be a separate field.",
    );
  }
  const targetIds = new Set(targets.map((f) => f.field_id));

  const hasCharacterConflict = fields.some(
    (f) =>
      isConnectedCharacterField(f) &&
      (f.field_id === source.field_id ||
        targetIds.has(f.connection.source_field_id)),
  );

  if (hasCharacterConflict) {
    throw new Error(
      "A connection source cannot also be used as a character box.",
    );
  }

  const hasTextFlowConflict = fields.some((f) => {
    if (!f.text_connection) return false;

    const textSourceId = f.text_connection.source_field_id;

    return (
      f.field_id === source.field_id ||
      textSourceId === source.field_id ||
      targetIds.has(f.field_id) ||
      targetIds.has(textSourceId)
    );
  });

  if (hasTextFlowConflict) {
    throw new Error(
      "These fields already participate in a text-flow connection. Disconnect that connection first.",
    );
  }
  // Avoid leaving half of an existing group behind when reconnecting a selection.
  const touchedGroups = new Set(
    targets.filter(isConnectedCharacterField).map((f) => f.connection.group_id),
  );
  if (
    fields.some(
      (f) =>
        isConnectedCharacterField(f) &&
        touchedGroups.has(f.connection.group_id) &&
        !targetKeys.has(key(f)),
    )
  ) {
    throw new Error(
      "Select every box in the existing connection before reconnecting it.",
    );
  }
  const indexByKey = new Map(targets.map((f, i) => [key(f), i]));
  const result = fields.map((f) =>
    indexByKey.has(key(f))
      ? {
          ...f,
          connection: {
            mode: "character",
            group_id: groupId,
            source_field_id: source.field_id,
            source_field_label:
              source.field_label || source.label || "Connected field",
            character_index: indexByKey.get(key(f)),
            character_count: targets.length,
            strip_separators: Boolean(stripSeparators),
            ...(text(staticPrefix).trim()
              ? { static_prefix: text(staticPrefix).trim() }
              : {}),
          },
        }
      : f,
  );
  validateConnectionGroups(result);
  const previousSource = getConnectedSourceFields(fields).find(
    (f) => f.field_id === source.field_id,
  );
  // Reordering an existing group retains the whole input and redistributes it.
  return previousSource
    ? applyConnectedCharacterValues(result, {
        [previousSource.name]: previousSource.value,
      })
    : result;
}

export function disconnectCharacterFields(fields, selected) {
  const keys = new Set(selected.map((f) => f.field_instance_id || f.name));
  const groups = new Set(
    fields
      .filter(
        (f) =>
          keys.has(f.field_instance_id || f.name) &&
          isConnectedCharacterField(f),
      )
      .map((f) => f.connection.group_id),
  );
  return fields.map((f) => {
    if (!isConnectedCharacterField(f) || !groups.has(f.connection.group_id))
      return f;
    const { connection, ...rest } = f;
    return rest;
  });
}
